import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Accordion, Carousel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faUserGraduate, faGlobe, faBookOpen, faArrowRight, faCheckCircle, faCertificate, faChalkboardTeacher, faChartLine, faQuestionCircle, faTools } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Hero, CourseCard } from '../../components/student';
import AuthModal from '../../components/auth/AuthModal';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { API_ORIGIN } from '../../services/api';

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');

  const openSignup = () => {
    setAuthMode('signup');
    setShowAuth(true);
  };

  const stats = [
    { count: '10M+', label: 'Learners', icon: faUserGraduate },
    { count: '170+', label: 'Countries', icon: faGlobe },
    { count: '500+', label: 'Courses', icon: faBookOpen },
    { count: '4.8/5', label: 'Rating', icon: faStar },
  ];

  type HomeCourse = { id: string | number; title: string; duration?: string; mode?: string; image?: string; logo?: string };
  const [trending, setTrending] = useState<HomeCourse[]>([]);
  const [allCourses, setAllCourses] = useState<any[]>([]);
  const [dsCourses, setDsCourses] = useState<HomeCourse[]>([]);
  const [careerPaths, setCareerPaths] = useState<any[]>([]);

  useEffect(() => {
    let mounted = true;
    async function loadTrending() {
      try {
        // Try to get cached data first (localStorage for persistence)
        const cachedAllCourses = localStorage.getItem('all_courses');
        let items: any[] = [];
        
        if (cachedAllCourses) {
          items = JSON.parse(cachedAllCourses);
          if (mounted) setAllCourses(items);
        }

        // Fetch fresh data in background or if cache empty
        const res = await api.courses.list('');
        items = (res.items || []) as any[];
        if (mounted) {
          localStorage.setItem('all_courses', JSON.stringify(items));
        }

        // Get trending IDs from settings (with localStorage fallback)
        let ids: number[] = [];
        try {
          const s = await api.settings.get();
          ids = Array.isArray(s?.trending_course_ids || s?.trendingCourses)
            ? (s.trending_course_ids || s.trendingCourses).map((n: any) => Number(n)).filter((n: any) => !isNaN(n))
            : [];
        } catch {
          try {
            const cached = localStorage.getItem('trending_course_ids');
            if (cached) ids = JSON.parse(cached);
          } catch { }
        }

        const resolveImageUrl = (u?: string | null) => {
          if (!u) return '';
          if (/^https?:\/\//i.test(u)) return u;
          return u.startsWith('/') ? `${API_ORIGIN}${u}` : `${API_ORIGIN}/${u}`;
        };
        
        if (mounted) {
          setAllCourses(items);
          const all: HomeCourse[] = items.map((c: any) => ({
            id: c.id,
            title: c.title,
            duration: c.duration || 'Self-paced',
            mode: c.level || 'Online',
            image: resolveImageUrl(c.image_url || ''),
            logo: '',
          }));
          
          // Sort by creation date (most recent first) and take only 4
          const sortedByDate = all.sort((a, b) => {
            const dateA = new Date(items.find(i => i.id === a.id)?.created_at || 0);
            const dateB = new Date(items.find(i => i.id === b.id)?.created_at || 0);
            return dateB.getTime() - dateA.getTime();
          });
          
          const recentCourses = sortedByDate.slice(0, 4);
          setTrending(recentCourses);
          
          const ds = all.filter(c => {
            const src = items.find(i => i.id === c.id) || {} as any;
            const cat = String(src.category || '');
            const title = String(src.title || c.title || '');
            const isDS = /data\s*science/i.test(cat) || /data\s*science/i.test(title);
            const isMBA = /mba/i.test(cat) || /mba/i.test(title);
            return isDS && !isMBA;
          });
          setDsCourses(ds.slice(0, 4));

          // Fetch Career Paths
          const paths = await api.careerPaths.list();
          if (mounted) setCareerPaths(paths.slice(0, 3));
        }
      } catch {
        if (mounted) setTrending([]);
      }
    }
    loadTrending();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="home-page">
      <AuthModal 
        show={showAuth} 
        onHide={() => setShowAuth(false)} 
        defaultMode={authMode} 
      />
      <Hero onGetStarted={openSignup} />

      {/* About Section - Modern Two-Column */}
      <section id="about" className="py-5" style={{ backgroundColor: '#F8F7FF' }}>
        <Container className="py-lg-5">
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <div className="mb-4">
                <span className="text-uppercase fw-bold" style={{ color: '#7C6EFF', letterSpacing: '2px', fontSize: '0.8rem' }}>Our Mission</span>
                <h2 className="fw-bold mt-2" style={{ color: '#1A1A2E', fontSize: '2.5rem' }}>Empowering the Next Generation of Data Leaders</h2>
              </div>
              <p style={{ color: '#6B7280', fontSize: '1.1rem', lineHeight: '1.7' }}>
                DataPylot isn't just another video platform. We are a project-first learning ecosystem designed to bridge the gap between academic theory and industry practice. 
              </p>
              <ul className="list-unstyled mt-4">
                {[
                  'Hands-on projects inspired by real industry challenges',
                  'Mentorship from senior data scientists and engineers',
                  'Interactive learning materials with dynamic notes'
                ].map((item, i) => (
                  <li key={i} className="mb-3 text-start" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '12px', color: '#1A1A2E', fontWeight: '600' }}>
                    <div style={{ flexShrink: 0, marginTop: '4px', color: '#00C9A7' }}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <span style={{ flexGrow: 1 }}>{item}</span>
                  </li>

                ))}
              </ul>

              <div className="mt-5">
                <Link to="/courses">
                  <Button className="rounded-pill px-5 py-3 fw-bold btn-cta-pill" style={{ border: 'none' }}>
                    Start Your Journey
                  </Button>
                </Link>
              </div>
            </Col>
            <Col lg={6}>
              <div className="position-relative">
                <img 
                  src="https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=1200&auto=format&fit=crop" 
                  alt="Students Working with Tech" 
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ transform: 'rotate(2deg)' }}
                />
                <div className="position-absolute bottom-0 start-0 bg-white p-4 rounded-4 shadow-lg m-4 d-none d-md-block" style={{ borderLeft: '5px solid #FF7A50' }}>
                  <h4 className="fw-bold mb-1" style={{ color: '#1A1A2E' }}>10k+</h4>
                  <p className="text-muted mb-0 small">Active Students Worldwide</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Popular Data Science Courses */}
      <section className="py-5" style={{ backgroundColor: '#ffffff' }}>
        <Container className="py-lg-4">
          <div className="text-center mb-4">
            <h2 className="fw-bold mb-3" style={{ color: '#1A1A2E', fontSize: '2.5rem' }}>Popular Data Science Courses</h2>
            <p className="mx-auto" style={{ color: '#6B7280', maxWidth: '700px', fontSize: '1.1rem' }}>
              Curated tracks to start or accelerate your data career. Learn from the best in the industry.
            </p>
          </div>
          <Row className="g-4">
            {allCourses.filter(c => /data\s*science/i.test(c.category)).slice(0, 3).map((c) => (
              <Col key={`ds-${c.id}`} lg={4} md={6}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <CourseCard course={c} />
                </motion.div>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-4">
            <Link to="/courses">
              <Button className="rounded-pill px-5 py-3 fw-bold" style={{ backgroundColor: '#7C6EFF', border: 'none' }}>
                Browse All Courses
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Career Paths Section */}
      <section className="py-5" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f0f7ff 100%)' }}>
        <Container className="py-lg-4">
          <div className="text-center mb-5">
            <Badge bg="primary-light" text="primary" className="mb-3 px-3 py-2 rounded-pill fw-bold text-uppercase" style={{ letterSpacing: '1px' }}>
              Career Roadmaps
            </Badge>
            <h2 className="fw-bold mb-3" style={{ color: '#1A1A2E', fontSize: '2.5rem' }}>Find Your <span className="text-primary">Dream Career</span></h2>
            <p className="mx-auto" style={{ color: '#6B7280', maxWidth: '700px', fontSize: '1.1rem' }}>
              Don't just take courses. Follow a structured journey designed by industry experts to get you hired.
            </p>
          </div>

          <Row className="g-4">
            {careerPaths.map((path) => (
              <Col key={path.id} lg={4} md={6}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <Card 
                    className="border-0 shadow-sm rounded-4 overflow-hidden h-100 transition-all hover-shadow-lg cursor-pointer"
                    onClick={() => navigate(`/career-path/${path.slug}`)}
                    style={{ background: '#fff' }}
                  >
                    <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                      <img 
                        src={path.thumbnail_image || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600'} 
                        alt={path.title} 
                        className="w-100 h-100 object-fit-cover transition-all"
                      />
                      <Badge bg="white" text="dark" className="position-absolute top-0 end-0 m-3 shadow-sm rounded-pill px-3 py-2 fw-bold">
                        {path.difficulty_level}
                      </Badge>
                    </div>
                    <Card.Body className="p-4 d-flex flex-column">
                      <h4 className="fw-bold mb-2">{path.title}</h4>
                      <p className="text-muted small mb-4 line-clamp-2">
                        {path.short_description}
                      </p>
                      <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2 text-primary fw-bold small">
                          <FontAwesomeIcon icon={faChartLine} /> Job Ready
                        </div>
                        <Button variant="link" className="p-0 text-decoration-none fw-bold d-flex align-items-center gap-1">
                          View Path <FontAwesomeIcon icon={faArrowRight} size="xs" />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-5">
            <Link to="/career-paths">
              <Button variant="outline-primary" className="rounded-pill px-5 py-3 fw-bold border-2">
                Explore All Roadmaps
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Why Choose Us */}
      <section className="py-5" style={{ backgroundColor: '#F8F7FF' }}>
        <Container className="py-lg-4">
          <div className="text-center mb-4">
            <h2 className="fw-bold mb-3" style={{ color: '#1A1A2E', fontSize: '2.5rem' }}>Why Choose DataPylot?</h2>
            <p className="mx-auto" style={{ color: '#6B7280', maxWidth: '700px', fontSize: '1.1rem' }}>
              We provide a modern learning experience designed for outcomes.
            </p>
          </div>
          <Row className="g-4">
            {[
              { title: 'Industry Mentorship', desc: 'Learn from practitioners at top tech companies.', icon: faChalkboardTeacher, color: '#7C6EFF' },
              { title: 'Interactive Notes', desc: 'Dynamic slides and hands-on learning materials.', icon: faBookOpen, color: '#FF7A50' },
              { title: 'Project-First', desc: 'Build a real portfolio that recruiters actually value.', icon: faChartLine, color: '#00C9A7' },
              { title: 'Verified Certificates', desc: 'Earn credentials recognized by industry leaders.', icon: faCertificate, color: '#7C6EFF' }
            ].map((f, i) => (
              <Col key={i} lg={3} md={6}>
                <div className="p-4 bg-white rounded-4 h-100 shadow-sm border-0 text-center transition-all hover-lift">
                  <div className="mb-4 d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: '70px', height: '70px', backgroundColor: `${f.color}15`, color: f.color, fontSize: '1.5rem' }}>
                    <FontAwesomeIcon icon={f.icon} />
                  </div>
                  <h5 className="fw-bold mb-3" style={{ color: '#1A1A2E' }}>{f.title}</h5>
                  <p className="small mb-0" style={{ color: '#6B7280' }}>{f.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Tools you will learn */}
      <section className="py-10 bg-white relative overflow-hidden">
        {/* Subtle decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary opacity-5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-warning opacity-5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>
        
        <Container>
          <div className="section-header text-center mb-4">
            <h2 className="section-title">Tools You Will Master</h2>
            <p className="section-subtitle text-muted max-w-2xl mx-auto mt-3">
              Get hands-on experience with the industry's most powerful data science and analytics tools.
            </p>
          </div>
          
          <Row className="g-4 justify-content-center">
            {[
              { name: 'python', color: '#3776AB', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg' },
              { name: 'numpy', color: '#013243', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/numpy/numpy-original.svg' },
              { name: 'pandas', color: '#150458', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pandas/pandas-original.svg' },
              { name: 'sklearn', color: '#F7931E', icon: 'https://upload.wikimedia.org/wikipedia/commons/0/05/Scikit_learn_logo_small.svg' },
              { name: 'tensorflow', color: '#FF6F00', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg' },
              { name: 'pytorch', color: '#EE4C2C', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg' },
              { name: 'sql', color: '#336791', icon: 'https://www.svgrepo.com/show/331760/sql-database-generic.svg' },
              { name: 'powerbi', color: '#F2C811', icon: 'https://upload.wikimedia.org/wikipedia/commons/c/cf/New_Power_BI_Logo.svg' }
            ].map((tool, i) => (
              <Col key={i} xs={6} sm={4} md={3} lg={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.2 }
                  }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="h-100"
                >
                  <div className="tool-card d-flex align-items-center gap-3 p-4 border rounded-4 bg-white shadow-sm h-100 transition-all border-light hover-shadow-lg">
                    <div 
                      className="tool-icon-box d-flex align-items-center justify-content-center rounded-3 shadow-sm bg-light" 
                      style={{ 
                        width: '56px', 
                        height: '56px', 
                        minWidth: '56px',
                        border: `1px solid ${tool.color}15`
                      }}
                    >
                      <img 
                        src={tool.icon} 
                        alt={tool.name} 
                        style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                        onError={(e) => {
                          // Fallback to faTools if icon fails
                          (e.target as any).style.display = 'none';
                          const parent = (e.target as any).parentElement;
                          const icon = document.createElement('i');
                          icon.className = 'fas fa-tools text-primary';
                          parent.appendChild(icon);
                        }}
                      />
                    </div>
                    <div>
                      <span className="fw-bold text-dark text-capitalize d-block">{tool.name}</span>
                      <small className="text-muted small">Professional</small>
                    </div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>




      {/* Recently Added Courses */}
      <section className="py-5 bg-white">
        <Container className="py-lg-3">
          <div className="section-header text-center mb-4">
            <h2 className="section-title">Recently Added Courses</h2>
            <p className="section-subtitle text-muted mx-auto" style={{ maxWidth: '600px', fontSize: '0.9rem' }}>
              Discover the latest courses added by our admin team to help you master in-demand skills.
            </p>
          </div>

          <Row className="g-3 g-md-4">
            {allCourses.slice(0, 4).map((c) => (
              <Col key={`recent-${c.id}`} xs={6} lg={3}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <CourseCard course={{
                    id: c.id,
                    title: c.title,
                    description: c.description || '',
                    category: c.category || 'Course',
                    thumbnail: c.image_url || 'https://placehold.co/800x450?text=Course',
                    lessons: c.lessons_count || 10,
                    enrolled: c.enrolled_count || 800,
                    rating: c.rating || 4.2,
                    tags: c.tags || ['New'],
                    regular_price: c.price || c.regular_price,
                    sale_price: c.sale_price
                  }} />
                </motion.div>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-4">
            <Link to="/courses">
              <Button className="btn-gl-primary btn-lg px-5">
                View All Courses
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* Partners Section */}
      <section id="partners" className="py-4 bg-white">
        <Container>
          <div className="section-header text-center mb-4">
            <h2 className="section-title">Our Partners</h2>
            <p className="section-subtitle text-muted">Empowering learning through strategic collaborations.</p>
          </div>
          <Row className="justify-content-center align-items-center g-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <Col key={i} xs={6} md={2} className="text-center opacity-50 hover-opacity-100 transition-all">
                <img 
                  src={`https://placehold.co/200x80?text=Partner+${i}`} 
                  alt={`Partner ${i}`} 
                  className="img-fluid grayscale" 
                  style={{ maxHeight: '40px' }}
                />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-10 bg-white border-top">
        <Container>
          <div className="section-header text-center mb-4">
            <h2 className="section-title">Contact Us</h2>
            <p className="section-subtitle text-muted">Have questions? We're here to help you navigate your journey.</p>
          </div>
          <Row className="g-5">
            <Col lg={6}>
              <div className="contact-info-card p-5 bg-light rounded-4 h-100 shadow-sm border">
                <h4 className="fw-bold mb-4 text-primary">Get in Touch</h4>
                <div className="d-flex align-items-start mb-4">
                  <div className="icon-box bg-white p-3 rounded-circle shadow-sm me-4 text-primary">
                    <FontAwesomeIcon icon={faTools} />
                  </div>
                  <div>
                    <h6 className="fw-bold mb-2">Our Office</h6>
                    <p className="text-muted mb-0">Ctronics Infotech Pvt. Ltd., Swanand, Ganesh Colony, Amravati 444605, MH, India</p>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-0">
                  <div className="icon-box bg-white p-3 rounded-circle shadow-sm me-4 text-success">
                    <FontAwesomeIcon icon={faQuestionCircle} />
                  </div>
                  <div>
                    <h6 className="fw-bold mb-2">Phone Numbers</h6>
                    <div className="row g-2">
                      <div className="col-6"><p className="text-muted mb-0">+91 9823540321</p></div>
                      <div className="col-6"><p className="text-muted mb-0">+91 9356409884</p></div>
                      <div className="col-6"><p className="text-muted mb-0">+91 9359653916</p></div>
                      <div className="col-6"><p className="text-muted mb-0">+91 8421298578</p></div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
               <div className="contact-form-wrapper p-5 bg-white rounded-4 shadow-lg border border-primary border-opacity-10 h-100">
                  <h4 className="fw-bold mb-4">Quick Message</h4>
                  <form>
                    <div className="mb-3">
                      <input type="text" className="form-control form-control-lg bg-light border-0" placeholder="Your Name" />
                    </div>
                    <div className="mb-3">
                      <input type="email" className="form-control form-control-lg bg-light border-0" placeholder="Email Address" />
                    </div>
                    <div className="mb-3">
                      <textarea className="form-control form-control-lg bg-light border-0" rows={4} placeholder="Your Message"></textarea>
                    </div>
                    <Button variant="primary" className="btn-lg w-100 py-3 fw-bold rounded-pill shadow-sm">Send Message</Button>
                  </form>
               </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section className="py-5 bg-light">
        <Container className="py-lg-3">
          <div className="section-header mb-4">
            <h2 className="section-title">Learner Reviews</h2>
            <p className="section-subtitle text-muted">
              See what our learners have to say about their experience.
            </p>
          </div>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <Card className="border-0 shadow-sm bg-white p-5">
                <div className="mb-4 text-warning home-stars">
                  {[...Array(5)].map((_, i) => <FontAwesomeIcon key={i} icon={faStar} />)}
                </div>
                <h4 className="fst-italic mb-4 text-dark">"The curriculum is well-structured and the mentors are extremely helpful. It helped me transition into a Data Science role seamlessly."</h4>
                <div>
                  <h6 className="fw-bold mb-0 text-dark">Sarah Johnson</h6>
                  <small className="text-muted">Data Analyst at TechCorp</small>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Home;
